/**
 * 個人レポート（Web管理者向け / 個人分析一覧から1名を選んで開く画面）
 *
 * 方針（組織レポート OrgReportPdfPage と同じ）:
 *  - 開いた時点で本文を「HTML」として画面内に表示する（PDF化はしない）。
 *  - 右上の「PDFとして保存」を押したときに初めて A4横PDF を生成・保存する。
 *  - 画面表示と単票PDFと全員PDFで、同じ Gb74PersonalReportPdf / 同じ描画経路を使う。
 *    こうしておくと「画面で見たもの」と「保存したPDF」が必ず一致する。
 *
 * ※ モバイルWebViewが読む /embed/report/employee/:userId（IndividualReportPage →
 *   Gb74PersonalReport の縦1カラム表示）は変更していない。A4横はスマホで読めないため、
 *   Web管理者向けの本画面だけを新設している。
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { analysisApi } from '../api/analysisApi';
import type { JudgmentPattern } from '../api/analysisApi';
import type { AnalysisResult } from '../types/analysis.types';
import { unwrapGb74Scores, getAxisScores } from '../utils/gb74Report';
import { downloadSingleReportPdf } from '../utils/bulkReportPdf';
import { Gb74PersonalReportPdf, PAGE_W } from './Gb74PersonalReportPdf';

/** 回答日の表示形式（PDF側 bulkReportPdf と揃える）。 */
const formatDate = (dateString: string): string =>
  new Date(dateString)
    .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/\//g, '.');

export const PersonalReportPdfPage = () => {
  const { userId = '' } = useParams<{ userId: string }>();

  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [pattern, setPattern] = useState<JudgmentPattern | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A4横ページ（PAGE_W px 固定）を、画面幅に合わせて縮小表示するための倍率。
  // 実寸のまま transform で縮めるだけなので、PDF と同じレイアウトが崩れない。
  const scalerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = scalerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / PAGE_W));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // 対象社員の最新結果と判定パターンを取得する（PDF側 renderUserReport と同じ順序）。
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!userId) {
        setError('社員が指定されていません。');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const latest = await analysisApi.getLatestResult(userId);
        const gb74 = unwrapGb74Scores(latest.scores?.scores);
        if (!gb74) {
          if (!aborted) {
            setError('この社員の分析結果は旧形式のため、この画面では表示できません。');
          }
          return;
        }

        // 文言（desc）はDBの判定パターン由来。取得失敗時も既定文言で描画できるので握りつぶす。
        let found: JudgmentPattern | null = null;
        try {
          const a = getAxisScores(gb74);
          found = await analysisApi.findPatternByScores({ A: a.A, B: a.B, C: a.C, D: a.D });
        } catch {
          found = null;
        }

        if (!aborted) {
          setResult(latest);
          setPattern(found);
        }
      } catch (e) {
        if (!aborted) {
          setError(e instanceof Error && e.message ? e.message : 'レポートの取得に失敗しました');
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    void load();
    return () => {
      aborted = true;
    };
  }, [userId]);

  const displayName = useMemo(() => result?.userName || userId, [result, userId]);

  // 表示中と同じ内容を A4横PDF にして保存する（押されたときだけ実行）。
  const handleSavePdf = useCallback(async () => {
    if (!result) return;
    try {
      setSaving(true);
      setError(null);
      await downloadSingleReportPdf(userId, displayName);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'PDFの生成に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [result, userId, displayName]);

  return (
    <Box sx={{ p: 2 }}>
      {/* ヘッダー: 右上に「PDFとして保存」 */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h5">
          個人レポート{result?.userName ? ` ${result.userName}` : ''}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
          onClick={handleSavePdf}
          disabled={!result || saving || loading}
        >
          {saving ? 'PDFを作成中…' : 'PDFとして保存'}
        </Button>
      </Box>

      {error && (
        <Alert severity={result ? 'error' : 'info'} sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
          <CircularProgress />
        </Box>
      )}

      {/* 本文: PDFと同じ A4横2ページを、画面幅に合わせて縮小表示する。 */}
      {!loading && result && (
        <Box ref={scalerRef} sx={{ width: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${PAGE_W}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              // 縮小した分だけ下に余白が残るのを詰める
              height: scale < 1 ? undefined : 'auto',
            }}
          >
            <Gb74PersonalReportPdf
              scores={result.scores.scores}
              userName={result.userName || userId}
              answerDate={formatDate(result.createdAt)}
              companyName={result.companyName || undefined}
              departmentName={result.departmentName || undefined}
              pattern={pattern}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PersonalReportPdfPage;
